'use client';

import React, { useRef, useEffect, useState } from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { useResponsive } from '@depop/web-ui-kit/useResponsive';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { DescriptionParser } from '@/app/[locale]/products/[slug]/components/Description/DescriptionParser';
import { ExtendedLink } from '@/components/ExtendedLink';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';
import { useProductTracking } from '@/modules/product/hooks/useProductTracking.ts';

type Props = {
  className?: string;
};

export function Description({ className = '' }: Props) {
  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const t = useTranslations('common');
  const { sendProductInteractionActionATEvent } = useProductTracking();
  const [shouldTruncate, setShouldTruncate] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { isMinMedium } = useResponsive();
  const { data: product } = useProductBySlugBasicPDP();

  function handleExpandedToggle() {
    sendProductInteractionActionATEvent(
      product?.user_id,
      isExpanded ? 'DescriptionShowLess' : 'DescriptionShowMore'
    );
    setIsExpanded((val) => !val);
  }

  function handleHashtagClick(hashTag: string) {
    sendProductInteractionActionATEvent(product?.user_id, 'ProductHashTag', {
      hashTag,
    });
  }

  useEffect(() => {
    const shouldTruncateDescription = descriptionRef.current
      ? descriptionRef.current.offsetHeight <
        descriptionRef.current.scrollHeight
      : false;

    setIsExpanded(false);
    setShouldTruncate(shouldTruncateDescription);
  }, [isMinMedium, product?.description]);

  if (!product?.description) {
    return null;
  }

  return (
    <div
      className={clsx({
        [styles.container]: true,
        [className]: Boolean(className),
        [styles['container--collapsed']]: !isExpanded,
        [styles['container--expanded']]: isExpanded,
      })}
    >
      <Body
        className={clsx({
          [styles.textWrapper]: true,
          [styles['textWrapper--collapsed']]: !isExpanded,
        })}
        ref={descriptionRef}
      >
        <DescriptionParser
          options={{
            '#': (value, key) => (
              <ExtendedLink
                key={key}
                href={`/search/?q=${value.replace('#', '%23')}`}
                className={styles.link}
                onClick={() => handleHashtagClick(value)}
              >
                {`${value} `}
              </ExtendedLink>
            ),
            '@': (value, key) => (
              <ExtendedLink
                key={key}
                href={`/${value.replace('@', '')}/`}
                className={styles.link}
              >
                {value}
              </ExtendedLink>
            ),
          }}
        >
          {product.description}
        </DescriptionParser>
      </Body>
      {(shouldTruncate || isExpanded) && (
        <div className={styles.showMoreContainer}>
          <button
            className={clsx({
              [styles.showMoreButton]: true,
              [styles['showMoreButton--expanded']]: isExpanded,
              [styles['showMoreButton--collapsed']]: !isExpanded,
            })}
            onClick={handleExpandedToggle}
          >
            <Body variant="sm-bold" as="span">
              {isExpanded
                ? t('ShowLess').toLowerCase()
                : t('ShowMore').toLowerCase()}
            </Body>
          </button>
        </div>
      )}
    </div>
  );
}
